import {HttpHeaders} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {ClientConfiguration} from "../../services/configuration/client.config";
import {FunctionAnyCallback, IXHRData} from "./http";
import {APIResult} from "../interaction/upload.service";

@Injectable()
export class StartupHttpService {
    private forwardHeaders: HttpHeaders;
    private CONTENT_TYPE_FORM_URLENCODED = { "Content-Type": "application/x-www-form-urlencoded" };

    constructor(private config: ClientConfiguration) { }

    public initialize() {
        this.forwardHeaders = this.getHeaders();
    }

    private getHeaders(headers?): HttpHeaders {
        return new HttpHeaders({ "X-Forwarded-Host": this.config.slcReferrerDomain, ...(headers || {}) });
    }

    private responseCallback = {
        text: (xhr: XMLHttpRequest) => ({success: this.isRequestSuccessful(xhr), data: xhr.responseText, status: xhr.status} as APIResult),
        json: (xhr: XMLHttpRequest) => ({success: this.isRequestSuccessful(xhr), data: xhr.response, status: xhr.status} as APIResult)
    };

    private isRequestSuccessful(xhr: XMLHttpRequest): boolean {
        // IE: sometimes 1223 instead of 204
        return (xhr.status === 0 ||
            (xhr.status >= 200 && xhr.status < 300) ||
            xhr.status === 304 || xhr.status === 1223);
    }

    private onReadyStateChange = (xhr: XMLHttpRequest, success: FunctionAnyCallback, failure: FunctionAnyCallback,
                                  responseType?: XMLHttpRequestResponseType) => () => {
            if (xhr.readyState === 4) {
                // @ts-ignore
                (xhr.status === 200 || window.cordova) ? success(this.responseCallback[responseType || "text"](xhr))
                    : failure(this.responseCallback[responseType || "text"](xhr));
            }
        }

    private handleOnCompleteSyncRequest(xhr: XMLHttpRequest, success: FunctionAnyCallback, failure: FunctionAnyCallback) {
        this.isRequestSuccessful(xhr) ? success(this.responseCallback[xhr.responseType || "text"](xhr))
            : failure(this.responseCallback[xhr.responseType || "text"](xhr));
    }

    private open(xhrData: IXHRData): void{
        // De-structure xhrData
        const { url, method, params, headers, responseType, async = false, withCredentials = true,
            success, failure, onReadyStateChange, onCompleteSyncCallback } = xhrData;

        // Initialize XHR
        let xhr = new XMLHttpRequest();
        xhr.withCredentials = withCredentials;
        if (responseType) xhr.responseType = responseType;

        // If onreadystatchange callback is defined, then set it up
        if (onReadyStateChange) xhr.onreadystatechange = onReadyStateChange(xhr, success, failure, responseType);

        // Open specified url with method and async flag
        xhr.open(method, url, async);

        // If headers are specified, then set them to the XHR
        if (headers) headers.keys().forEach(key => headers.getAll(key)?.forEach(header =>  xhr.setRequestHeader(key, header)));

        // Send the request
        xhr.send(params);

        // If onCompleteSyncCallback callback is defined, then call it with the XHR
        if (onCompleteSyncCallback) onCompleteSyncCallback(xhr, success, failure);
    }

    public getJSONResponse(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "GET", responseType: "json", async: true, success, failure,
            onReadyStateChange: this.onReadyStateChange });
    }

    public getJSONResponseWithReferrer(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "GET", headers: this.forwardHeaders, responseType: "json", async: true, success, failure,
            onReadyStateChange: this.onReadyStateChange });
    }

    public get(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "GET", async: true, success, failure,
            onReadyStateChange: this.onReadyStateChange });
    }

    public getWithReferrer(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "GET", headers: this.forwardHeaders, async: true, success, failure,
            onReadyStateChange: this.onReadyStateChange });
    }

    public post(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "POST", success, failure,
            onCompleteSyncCallback: (xhr, success, failure) => this.handleOnCompleteSyncRequest(xhr, success, failure) });
    }

    // For now this only send x-www-form-urlencoded content, but can be parameterized
    public postWithReferrer(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback,
        params?: string): void {
        this.open({ url, method: "POST", params, headers: this.getHeaders(this.CONTENT_TYPE_FORM_URLENCODED), success, failure,
            onCompleteSyncCallback: (xhr, success, failure) => this.handleOnCompleteSyncRequest(xhr, success, failure) });
    }

    public delete(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "DELETE", success, failure,
            onCompleteSyncCallback: (xhr, success, failure) => this.handleOnCompleteSyncRequest(xhr, success, failure) });
    }

    public deleteWithReferrer(url: string, success: FunctionAnyCallback, failure: FunctionAnyCallback): void {
        this.open({ url, method: "DELETE", headers: this.forwardHeaders, success, failure,
            onCompleteSyncCallback: (xhr, success, failure) => this.handleOnCompleteSyncRequest(xhr, success, failure) });
    }
}
